import React, { useState, useRef, useEffect } from 'react';
import { Container, Stack, Offcanvas, Toast, ToastContainer, Collapse } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbars, { APP_NAME } from '../header/Navbar';
import Footer from '../footer/Footer';
import NavbarItem from '../header/NavbarItem';
import { styles, getLayoutRoot } from './AppLayoutStyles';
import { useDrawer } from '../../context/DrawerContext';
import { useTheme } from '../../context/ThemeContext';
import { useAlert } from '../../context/AlertContext';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../modals/ConfirmModal';
import GlobalModals from '../modals/GlobalModals';
import DevelopmentBanner from '../ui/feedback/DevelopmentBanner';
import ErrorLogger from '../../services/ErrorLogger';
import { useISLR } from '../../hooks/useISLR';
import { useIVA } from '../../hooks/useIVA';
import ISLRExportModal from '../../page/app/retenciones/I.S.L.R/ISLRExportModal';
import IVAExportModal from '../../page/app/retenciones/i.v.a/IVAExportModal';


function LeftDrawer({ open, closeDrawer, navigate, logout, onExportISLR, onExportIVA }) {
  const [exportOpen, setExportOpen] = useState(false);
  return (
    <Offcanvas show={open} onHide={closeDrawer} placement="start">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="d-flex align-items-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Logo"
            className="app-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className={styles.classNavTitle}>{APP_NAME}</span>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <NavbarItem vertical isOpen={open} onClick={closeDrawer} />

        {/* Export Mobile Menu */}
        <hr className="my-3 text-muted" />
        <div
          className={`nav-item-link d-flex align-items-center gap-2`}
          onClick={() => setExportOpen(!exportOpen)}
        >
          <i className="bi bi-download me-2"></i>
          Exportar
          <i className={`bi ms-auto navbar-item-icon ${exportOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        </div>
        <Collapse in={exportOpen}>
          <div>
            <div
              className="nav-item-link ps-5"
              onClick={() => { closeDrawer(); onExportISLR(); }}
            >
              <i className="bi bi-filetype-xml me-2 text-primary"></i>
              Retenciones ISLR (XML)
            </div>
            <div
              className="nav-item-link ps-5"
              onClick={() => { closeDrawer(); onExportIVA(); }}
            >
              <i className="bi bi-file-text me-2 text-success"></i>
              Retenciones IVA (TXT)
            </div>
          </div>
        </Collapse>

        {/* User Options Section */}
        <hr className="my-3 text-muted" />
        <div className="d-flex flex-column gap-2">
          <div
            className="nav-item-link"
            onClick={() => { closeDrawer(); navigate('/configuracion'); }}
          >
            <i className="bi bi-gear me-2"></i>
            Configuración
          </div>
          <div
            className="nav-item-link text-danger"
            onClick={() => { closeDrawer(); logout(); }}
          >
            <i className="bi bi-box-arrow-right me-2"></i>
            Cerrar Sesión
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

function RightDrawer({ open, content, closeDrawer, title, container, width, requestClose }) {
  // Use Bootstrap CSS variable so Offcanvas internals respect the width
  const style = { '--bs-offcanvas-width': width };
  const offcanvasRef = useRef(null);

  useEffect(() => {
    try {
      if (offcanvasRef.current && width) {
        // set inline width to ensure it takes effect over other CSS
        offcanvasRef.current.style.width = width;
        // also set the CSS variable as backup
        offcanvasRef.current.style.setProperty('--bs-offcanvas-width', width);
      }
    } catch (e) {
      // ignore
    }
  }, [width]);

  return (
    <Offcanvas ref={offcanvasRef} data-drawer-width={width} show={open} onHide={requestClose} placement="end" style={style}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="d-flex align-items-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Logo"
            className="app-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {(title || APP_NAME)}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="app-sidebar-body">
        <div className="app-sidebar-content">
          {content}
        </div>
      </Offcanvas.Body>
      <Footer fixed={false} />
    </Offcanvas>
  );
}

function Layout({ children }) {
  const { open, content, closeDrawer, title, requestClose, options } = useDrawer();
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState('700px');
  const { dark } = useTheme();

  // Export State
  const [showISLRModal, setShowISLRModal] = useState(false);
  const [showIVAModal, setShowIVAModal] = useState(false);

  // Export Hooks
  const { retenciones: islrData, fetchRetenciones: fetchISLR } = useISLR();
  const { retenciones: ivaData, fetchRetenciones: fetchIVA } = useIVA();

  const handleOpenISLR = () => {
    fetchISLR(true);
    setShowISLRModal(true);
  };

  const handleOpenIVA = () => {
    fetchIVA(true);
    setShowIVAModal(true);
  };

  const handleDrawerOpen = () => setShowDrawer(true);
  const handleDrawerClose = () => setShowDrawer(false);

  const location = useLocation();


  const mainContainerRef = useRef(null);

  // Global error handlers
  useEffect(() => {
    const handleError = (event) => {
      ErrorLogger.log(event.error || new Error(event.message), {
        type: 'Global JavaScript Error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    };

    const handleRejection = (event) => {
      ErrorLogger.log(new Error(event.reason), {
        type: 'Unhandled Promise Rejection',
        promise: String(event.promise)
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  useEffect(() => {
    // Fix mobile viewport height issues by setting a --vh variable
    const setVh = () => {
      try {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      } catch (e) {
        // ignore during SSR or environments without window
      }
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  // Compute drawer width responsively
  useEffect(() => {
    const updateWidth = () => {
      try {
        const w = window.innerWidth;
        // On very small screens use full width to behave like a modal
        if (w < 576) {
          setDrawerWidth('100%');
          return;
        }
        // Use a percentage but cap at 700px
        // const calc = Math.min(700, Math.floor(w * 0.7));
        // console.log('Drawer width:', `${calc}px`);
        setDrawerWidth(`70%`);
      } catch (e) {
        // ignore
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const { alertState, hideAlert, toastState, hideToast } = useAlert();

  const handleConfirm = () => {
    if (alertState.onConfirm) {
      alertState.onConfirm();
    }
    hideAlert();
  };

  // Determine environment for padding adjustments
  const isDevelopment =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.');

  // Calculate top padding (Navbar only vs Navbar + Banner)
  const paddingTop = isDevelopment ? '90px' : '65px'; // 56px navbar + ~34px banner

  return (
    <Stack
      style={getLayoutRoot(dark)}
    // className={styles.classLayoutRoot}
    >
      <Navbars
        onMenuClick={handleDrawerOpen}
        onExportISLR={handleOpenISLR}
        onExportIVA={handleOpenIVA}
      />

      {/* Global Toast Notification */}
      <ToastContainer position="top-end" className="p-3 app-toast-container">
        <Toast
          show={toastState.show}
          onClose={hideToast}
          bg={toastState.variant}
        >
          <Toast.Header closeButton={true}>
            <strong className="me-auto">
              {toastState.variant === 'success' ? 'Éxito' :
                toastState.variant === 'danger' ? 'Error' :
                  toastState.variant === 'warning' ? 'Advertencia' : 'Información'}
            </strong>
          </Toast.Header>
          <Toast.Body className={toastState.variant === 'light' ? '' : 'text-white'}>
            {toastState.message}
            {toastState.details && process.env.NODE_ENV === 'development' && (
              <div className="mt-2 pt-2 border-top border-white-50 small app-toast-message">
                <strong>Debug Info:</strong><br />
                {toastState.details}
              </div>
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <LeftDrawer
        open={showDrawer}
        closeDrawer={handleDrawerClose}
        navigate={useNavigate()}
        logout={useAuth().logout}
        onExportISLR={handleOpenISLR}
        onExportIVA={handleOpenIVA}
      />
      <RightDrawer open={open} content={content} closeDrawer={closeDrawer} title={title} container={mainContainerRef.current} width={options?.width || drawerWidth} requestClose={requestClose} />

      <Container fluid ref={mainContainerRef} style={{ ...styles.layoutContainer, paddingTop }} className="app-layout-content">
        {children}
      </Container>
      <Footer fixed={true} />

      {/* Global Confirm Modal */}
      <ConfirmModal
        show={alertState.show}
        onHide={hideAlert}
        title={alertState.title}
        message={alertState.message}
        alertVariant={alertState.alertVariant}
        confirmLabel={alertState.confirmLabel}
        cancelLabel={alertState.cancelLabel}
        confirmVariant={alertState.confirmVariant}
        onConfirm={handleConfirm}
      />

      {/* Global Modals */}
      <GlobalModals />

      {/* Export Modals (Centralized) */}
      <ISLRExportModal
        show={showISLRModal}
        onHide={() => setShowISLRModal(false)}
        data={islrData}
      />

      <IVAExportModal
        show={showIVAModal}
        onHide={() => setShowIVAModal(false)}
        data={ivaData}
      />
    </Stack>
  );
}

export default Layout;